/**
 * Two distinct beam renderers sharing one frame batch:
 *  - sniper SIGHTS (`add`): a thin red telegraph line tracing where a sniper's next
 *    shell will ricochet. A small pool of reused THREE.Line objects, retraced each
 *    frame; purely a warning, no width needed.
 *  - laser ENEMY beams (`drawPath`): a thick, glowing magenta laser. WebGL ignores
 *    line width, so these are built from real geometry — a "+"-section ribbon (a flat
 *    quad strip crossed with a vertical one) per beam so it reads as a solid bolt from
 *    the angled camera, with a wide translucent glow under a hot near-white core.
 */

import * as THREE from "three";
import { BULLET } from "../config";
import { reflectCircle } from "../world/gamemap";
import type { Arena } from "../world/arena";

const MAX_PTS = 12; // muzzle + bounces + endpoint
const MAX_SEG = MAX_PTS - 1; // ribbon segments between consecutive points
const RANGE = 8000; // safety cap = a shell's max reach (bulletSpeed 1000 * SNIPER_LIFE 8); the trace stops early at the target, so this only bounds a momentarily-stale solution

export class Lasers {
  private lines: THREE.Line[] = [];
  private buffers: Float32Array[] = [];
  private used = 0;
  private readonly N = 22; // > the enemy field cap (16) so live damaging beams never lose a slot to sniper sights
  private readonly _p = { x: 0, z: 0 };
  private readonly _v = { x: 0, z: 0 };
  private _arena: Arena | null = null;

  // thick damaging-beam pool: a glow ribbon + a brighter core ribbon per beam
  private glow: THREE.Mesh[] = [];
  private core: THREE.Mesh[] = [];
  private glowBuf: Float32Array[] = [];
  private coreBuf: Float32Array[] = [];
  private beamUsed = 0;

  constructor(scene: THREE.Scene) {
    for (let i = 0; i < this.N; i++) {
      const buf = new Float32Array(MAX_PTS * 3);
      const geom = new THREE.BufferGeometry();
      geom.setAttribute("position", new THREE.BufferAttribute(buf, 3));
      geom.setDrawRange(0, 0);
      const mat = new THREE.LineBasicMaterial({ color: 0xff2a2a, transparent: true, opacity: 0.5, depthWrite: false });
      const line = new THREE.Line(geom, mat);
      line.frustumCulled = false;
      line.renderOrder = 3;
      line.visible = false;
      scene.add(line);
      this.lines.push(line);
      this.buffers.push(buf);

      // glow (wide, soft halo) then core (narrow, hot center) — additive so they read as light
      const glow = this.makeRibbon(scene, 0xff3df0, 0.34, 3);
      const core = this.makeRibbon(scene, 0xffd2ff, 0.95, 4);
      this.glow.push(glow.mesh); this.glowBuf.push(glow.buf);
      this.core.push(core.mesh); this.coreBuf.push(core.buf);
    }
  }

  /** Build one reusable "+"-section ribbon mesh (8 verts / 12 indices per segment). */
  private makeRibbon(scene: THREE.Scene, color: number, opacity: number, renderOrder: number) {
    const buf = new Float32Array(MAX_SEG * 8 * 3);
    const idx = new Uint16Array(MAX_SEG * 12);
    for (let s = 0; s < MAX_SEG; s++) {
      const b = s * 8, o = s * 12;
      // horizontal quad (verts b..b+3)
      idx[o] = b; idx[o + 1] = b + 1; idx[o + 2] = b + 2;
      idx[o + 3] = b + 2; idx[o + 4] = b + 1; idx[o + 5] = b + 3;
      // vertical quad (verts b+4..b+7)
      idx[o + 6] = b + 4; idx[o + 7] = b + 5; idx[o + 8] = b + 6;
      idx[o + 9] = b + 6; idx[o + 10] = b + 5; idx[o + 11] = b + 7;
    }
    const geom = new THREE.BufferGeometry();
    geom.setAttribute("position", new THREE.BufferAttribute(buf, 3));
    geom.setIndex(new THREE.BufferAttribute(idx, 1));
    geom.setDrawRange(0, 0);
    const mat = new THREE.MeshBasicMaterial({
      color, transparent: true, opacity, depthWrite: false,
      blending: THREE.AdditiveBlending, side: THREE.DoubleSide,
    });
    const mesh = new THREE.Mesh(geom, mat);
    mesh.frustumCulled = false;
    mesh.renderOrder = renderOrder;
    mesh.visible = false;
    scene.add(mesh);
    return { mesh, buf };
  }

  /** Start a frame's batch of laser draws. */
  begin(arena: Arena | null): void {
    this.used = 0;
    this.beamUsed = 0;
    this._arena = arena;
  }

  /** Trace one beam from (mx,my,mz) along (dirx,dirz) for `bounces` ricochets. If a
   *  target (tx,tz) is given, the trace STOPS there — so the sniper's sight shows the
   *  exact path its shell takes to the player (same step + bounces as the bank solver). */
  add(mx: number, my: number, mz: number, dirx: number, dirz: number, level: number, bounces: number, tx?: number, tz?: number): void {
    if (this.used >= this.N) return;
    const buf = this.buffers[this.used];
    const line = this.lines[this.used];
    const pos = this._p, vel = this._v;
    const len = Math.hypot(dirx, dirz) || 1;
    pos.x = mx; pos.z = mz; vel.x = dirx / len; vel.z = dirz / len;
    (line.material as THREE.LineBasicMaterial).color.setHex(0xff2a2a); // sniper SIGHT: dim red telegraph
    (line.material as THREE.LineBasicMaterial).opacity = 0.5;
    let n = 0;
    const put = (i: number, x: number, z: number) => { buf[i * 3] = x; buf[i * 3 + 1] = my; buf[i * 3 + 2] = z; };
    put(n++, mx, mz);
    let bl = bounces, traveled = 0;
    const step = 5;
    const hasT = tx !== undefined && tz !== undefined;
    while (traveled < RANGE && n < MAX_PTS - 1) {
      pos.x += vel.x * step; pos.z += vel.z * step; traveled += step;
      if (hasT) { const ex = tx! - pos.x, ez = tz! - pos.z; if (ex * ex + ez * ez <= 12 * 12) { put(n++, pos.x, pos.z); break; } } // reached the target — stop exactly where the solver counts a hit (12)
      let hit = reflectCircle(pos, vel, BULLET.radius, level === 0);
      if (this._arena && this._arena.reflectShell(pos, vel, BULLET.radius, level)) hit = true;
      if (hit) { put(n++, pos.x, pos.z); if (--bl < 0) break; }
    }
    if (bl >= 0 && n < MAX_PTS) put(n++, pos.x, pos.z);
    for (let i = n; i < MAX_PTS; i++) put(i, pos.x, pos.z);
    line.geometry.setDrawRange(0, n);
    (line.geometry.getAttribute("position") as THREE.BufferAttribute).needsUpdate = true;
    line.visible = true;
    this.used++;
  }

  /** A straight BLUE shield-link beam (Aegis → the ally it's protecting). Reuses the
   *  line pool — purely cosmetic, not a damaging beam. */
  link(ax: number, ay: number, az: number, bx: number, by: number, bz: number): void {
    if (this.used >= this.N) return;
    const buf = this.buffers[this.used];
    const line = this.lines[this.used];
    (line.material as THREE.LineBasicMaterial).color.setHex(0x3fc8ff); // shield blue
    (line.material as THREE.LineBasicMaterial).opacity = 0.9;
    const put = (i: number, x: number, y: number, z: number) => { buf[i * 3] = x; buf[i * 3 + 1] = y; buf[i * 3 + 2] = z; };
    put(0, ax, ay, az); put(1, bx, by, bz);
    for (let i = 2; i < MAX_PTS; i++) put(i, bx, by, bz);
    line.geometry.setDrawRange(0, 2);
    (line.geometry.getAttribute("position") as THREE.BufferAttribute).needsUpdate = true;
    line.visible = true;
    this.used++;
  }

  /** Render a precomputed polyline (flat [x,z,x,z,...], `n` points) at height `my` as
   *  the laser ENEMY's beam — a thick glowing bolt. `alpha` scales the opacity (a laser
   *  fades its beam in over its charge); `glowHex`/`coreHex` override the colour (charge =
   *  red→pink, live beam = the default magenta/hot-white). */
  drawPath(pts: number[], n: number, my: number, alpha = 1, glowHex = 0xff3df0, coreHex = 0xffd2ff): void {
    if (this.beamUsed >= this.N || n < 2) return;
    const m = n > MAX_PTS ? MAX_PTS : n;
    const gm = this.glow[this.beamUsed].material as THREE.MeshBasicMaterial;
    const cm = this.core[this.beamUsed].material as THREE.MeshBasicMaterial;
    gm.color.setHex(glowHex); gm.opacity = 0.34 * alpha;
    cm.color.setHex(coreHex); cm.opacity = 0.95 * alpha;
    this.fillRibbon(this.glowBuf[this.beamUsed], pts, m, my, 11); // wide halo
    this.fillRibbon(this.coreBuf[this.beamUsed], pts, m, my, 4); // hot core
    const segs = m - 1;
    for (const arr of [this.glow, this.core]) {
      const mesh = arr[this.beamUsed];
      mesh.geometry.setDrawRange(0, segs * 12);
      (mesh.geometry.getAttribute("position") as THREE.BufferAttribute).needsUpdate = true;
      mesh.visible = true;
    }
    this.beamUsed++;
  }

  /** Fill a "+"-section ribbon: per segment, a flat (XZ) quad crossed with a vertical one. */
  private fillRibbon(buf: Float32Array, pts: number[], m: number, my: number, hw: number): void {
    for (let i = 0; i < m - 1; i++) {
      const ax = pts[i * 2], az = pts[i * 2 + 1];
      const bx = pts[(i + 1) * 2], bz = pts[(i + 1) * 2 + 1];
      let dx = bx - ax, dz = bz - az;
      const dl = Math.hypot(dx, dz) || 1; dx /= dl; dz /= dl;
      const px = -dz * hw, pz = dx * hw; // horizontal perpendicular (in XZ)
      let o = i * 8 * 3;
      const v = (x: number, y: number, z: number) => { buf[o++] = x; buf[o++] = y; buf[o++] = z; };
      // horizontal quad
      v(ax + px, my, az + pz); v(ax - px, my, az - pz);
      v(bx + px, my, bz + pz); v(bx - px, my, bz - pz);
      // vertical quad (offset along world-up)
      v(ax, my + hw, az); v(ax, my - hw, az);
      v(bx, my + hw, bz); v(bx, my - hw, bz);
    }
  }

  /** Hide any beams not drawn this frame. */
  end(): void {
    for (let i = this.used; i < this.N; i++) this.lines[i].visible = false;
    for (let i = this.beamUsed; i < this.N; i++) { this.glow[i].visible = false; this.core[i].visible = false; }
  }

  clear(): void {
    this.used = 0;
    this.beamUsed = 0;
    for (const l of this.lines) l.visible = false;
    for (const g of this.glow) g.visible = false;
    for (const c of this.core) c.visible = false;
  }
}
